/** Public, provider-free booking clearance contract shared by web and native. */
export type BookingDriverClearance = "approved" | "verification_required" | "verification_pending" |
  "in_review" | "invitation_pending" | "rejected" | "inactive" | "unknown";
export interface BookingDriverOverview {
  primary: BookingDriverClearance | "not_selected";
  viewer: BookingDriverClearance | "not_driver";
  viewerRole: "primary" | "additional" | null;
  anyApprovedDriver: boolean;
  evidenceAvailable: boolean;
}
export interface DriverDisplaySummary {
  attemptsUnavailable?: boolean;
  licenceReuploadRequested?: boolean;
  driverClearance?: BookingDriverOverview;
  action: "start" | "resume" | "none";
}
const ACTIVE = new Set(["pending", "approved", "confirmed", "trip_confirmed"]);

/** Never infer clearance from reservation, payment, account flags, or a legacy `complete`. */
export function bookingDriverNextStep(status: string, summary?: DriverDisplaySummary | null, usSelfDrive = false) {
  if (!ACTIVE.has(status.toLowerCase())) return null;
  const evidence = summary?.driverClearance;
  if (!evidence) return usSelfDrive
    ? { key: "driver_clearance.unavailable", text: "Driver clearance could not be loaded", action: "drivers" as const } : null;
  // Overview work follows the selected primary, never an optional additional gap.
  const state = evidence.primary;
  const ownPrimary = evidence.viewerRole === "primary";
  let key: string;
  let text: string;
  let action: "start" | "resume" | "drivers" | "none" = "none";
  if (!evidence.evidenceAvailable || state === "unknown" || (ownPrimary && summary?.attemptsUnavailable)) {
    key = "unavailable"; text = "Driver clearance could not be loaded";
    action = "drivers";
  } else if (state === "not_selected") {
    key = "choose"; text = "Choose a primary driver"; action = "drivers";
  } else if (state === "invitation_pending") {
    key = "invite"; text = "Primary driver needs to accept their invitation"; action = "drivers";
  } else if (ownPrimary && summary?.action === "resume") {
    key = "resume"; text = "Continue your driver’s licence verification"; action = "resume";
  } else if (ownPrimary && summary?.licenceReuploadRequested === true && summary.action === "start") {
    // An explicit fresh-document request must not be hidden by retained
    // clearance from an earlier check. The server still owns eligibility.
    key = "verify"; text = "Verify your driver’s licence"; action = "start";
  } else if (state === "in_review" || state === "verification_pending") {
    key = "review"; text = "Driver verification is being reviewed — no action needed now";
    action = ownPrimary ? "none" : "drivers";
  } else if (state === "approved") {
    key = "approved"; text = "Driver approved for this booking";
    action = ownPrimary ? "none" : "drivers";
  } else if (state === "rejected" || state === "inactive") {
    key = "attention"; text = "Driver clearance needs attention"; action = "drivers";
    if (ownPrimary && summary?.action === "start") action = "start";
  } else if (ownPrimary && summary?.action === "start") {
    key = "verify"; text = "Verify your driver’s licence"; action = "start";
  } else {
    key = ownPrimary ? "checks_required" : "primary_verify";
    text = ownPrimary ? "Booking-specific driver checks are still required" : "Primary driver needs to complete verification";
    action = "drivers";
  }
  return { key: `driver_clearance.${key}`, text, action };
}

/** An individual's verification entry still permits their own additional-driver checks. */
export function bookingViewerDriverNextStep(status: string, summary?: DriverDisplaySummary | null, usSelfDrive = false) {
  const overview = summary?.driverClearance;
  return bookingDriverNextStep(status, overview?.viewerRole != null ? {
    ...summary!, driverClearance: { ...overview, primary: overview.viewer as BookingDriverClearance, viewerRole: "primary" },
  } : summary, usSelfDrive);
}
